import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ArrowLongUpIcon, ArrowLongDownIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import { getNotes, setSelectedMedicalSummary, setSelectedRowId } from "../../../../redux/modules/medical-summary/actions";
import { formatDateToDD_MMM_YYYY } from "../../../../helpers/functions";
import NotesList from "../../../forms/components/NotesList";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";

const Notes = ({ patient, currentTabIndex = 0 }) => {
  const dispatch = useDispatch();
  const { id: patientId, summaryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { notesResponse, loading, selectedRowId: reduxSelectedRowId } = useSelector(
    (state) => state.medicalSummary
  );

  const [date, setDateRange] = useState("last6Months");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [modalNote, setModalNote] = useState(null);
  const [sort, setSort] = useState("date");
  const [order, setOrder] = useState("desc");
  const [showDateMenu, setShowDateMenu] = useState(false);
  const selectedRowId = reduxSelectedRowId;
  const lastSelectionSignatureRef = useRef(null);

  const toDateKey = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().split("T")[0];
  };

  const groupedNotes = useMemo(() => {
    const notes = Array.isArray(notesResponse) ? notesResponse : [];
    const map = new Map();

    notes.forEach((note, index) => {
      const dateKey = toDateKey(note?.systemPeriod || note?.date) || `nodate-${index}`;
      const rowId = `notes-${dateKey}`;
      const performedBy = note?.performedBy || "Vesna Spadafora";

      if (!map.has(rowId)) {
        map.set(rowId, {
          rowId,
          dateKey,
          performedBy,
          notes: [note],
        });
        return;
      }

      const group = map.get(rowId);
      group.notes.push(note);
    });

    const rows = Array.from(map.values());
    rows.sort((a, b) => {
      if (sort === "performedBy") {
        const pa = String(a?.performedBy || "").toLowerCase();
        const pb = String(b?.performedBy || "").toLowerCase();
        return order === "asc" ? pa.localeCompare(pb) : pb.localeCompare(pa);
      }

      const da = a?.dateKey && !String(a.dateKey).startsWith("nodate-") ? new Date(a.dateKey) : new Date(0);
      const db = b?.dateKey && !String(b.dateKey).startsWith("nodate-") ? new Date(b.dateKey) : new Date(0);
      return order === "asc" ? da - db : db - da;
    });

    return rows;
  }, [notesResponse, sort, order]);

  const groupedNotesById = useMemo(() => {
    const map = new Map();
    groupedNotes.forEach((group) => map.set(group.rowId, group));
    return map;
  }, [groupedNotes]);

  const getNotesData = useCallback(() => {
    if (patient?.id) {
      const filter = {
        fromDate: fromDate ? fromDate.toISOString() : null,
        toDate: toDate ? toDate.toISOString() : null,
      };
      dispatch(
        getNotes(patient.id, filter)
      );
    }
  }, [patient, fromDate, toDate, dispatch]);

  useEffect(() => {
    // default last 6 months
    const now = new Date();
    const sixMonthsBack = new Date();
    sixMonthsBack.setMonth(now.getMonth() - 6);
    setFromDate(sixMonthsBack);
    setToDate(now);
    setDateRange("last6Months");
  }, []);

  // Separate useEffect to call API when patient is available
  useEffect(() => {
    if (patient?.id && fromDate && toDate) {
      getNotesData();
    }
  }, [patient, fromDate, toDate, getNotesData, dispatch]);

  // Single selection sync effect:
  // URL selectedRowId -> summaryId (group row id) -> Redux selectedRowId
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlSelectedRowId = urlParams.get("selectedRowId");
    const summaryGroupRowId = summaryId && String(summaryId).startsWith("notes-") ? summaryId : null;
    const nextSelectedRowId = urlSelectedRowId || summaryGroupRowId || reduxSelectedRowId || null;

    if (!nextSelectedRowId) {
      if (lastSelectionSignatureRef.current !== null) {
        setSelectedSummary(null);
        dispatch(setSelectedMedicalSummary(null));
        lastSelectionSignatureRef.current = null;
      }
      return;
    }

    const selectedGroup = groupedNotesById.get(nextSelectedRowId);
    if (!selectedGroup) return;

    if (reduxSelectedRowId !== nextSelectedRowId) {
      dispatch(setSelectedRowId(nextSelectedRowId));
    }

    const signature = `${nextSelectedRowId}:${selectedGroup.notes.length}:${selectedGroup.notes[0]?.id || ""}`;
    if (lastSelectionSignatureRef.current !== signature) {
      setSelectedSummary(selectedGroup.notes);
      dispatch(setSelectedMedicalSummary(selectedGroup.notes));
      lastSelectionSignatureRef.current = signature;
    }
  }, [location.search, summaryId, reduxSelectedRowId, groupedNotesById, dispatch]);

  const handleRowSelect = useCallback((group) => {
    if (!group?.rowId) return;

    const signature = `${group.rowId}:${group.notes?.length || 0}:${group.notes?.[0]?.id || ""}`;
    if (reduxSelectedRowId !== group.rowId) {
      dispatch(setSelectedRowId(group.rowId));
    }

    setSelectedSummary(group.notes || []);
    dispatch(setSelectedMedicalSummary(group.notes || []));
    lastSelectionSignatureRef.current = signature;
  }, [reduxSelectedRowId, dispatch]);

  // Clear selection when the parent tab changes (skip initial mount)
  const prevTabRef = React.useRef(null);
  useEffect(() => {
    const prev = prevTabRef.current;
    if (prev !== null && prev !== currentTabIndex) {
      setSelectedSummary(null);
      dispatch(setSelectedMedicalSummary(null));
      dispatch(setSelectedRowId(null));
      lastSelectionSignatureRef.current = null;
    }
    prevTabRef.current = currentTabIndex;
  }, [currentTabIndex, dispatch]);

  const handleOpenDateRange = () => {
    setShowDateMenu((s) => !s);
  };

  const computeRangeForOption = (value) => {
    const now = new Date();
    let from = null;
    if (value === "last4Weeks") {
      from = new Date(now); from.setDate(now.getDate() - 28);
    } else if (value === "last3Months") {
      from = new Date(now); from.setMonth(now.getMonth() - 3);
    } else if (value === "last6Months") {
      from = new Date(now); from.setMonth(now.getMonth() - 6);
    } else if (value === "last12Months") {
      from = new Date(now); from.setFullYear(now.getFullYear() - 1);
    }
    return { from, to: now };
  };

  const handleSortOrder = (newSort, newOrder) => {
    setSort(newSort);
    setOrder(newOrder);
  };

  const dateRangeOptions = [
    { value: "last4Weeks", label: "Last 4 weeks" },
    { value: "last3Months", label: "Last 3 months" },
    { value: "last6Months", label: "Last 6 months" },
    { value: "last12Months", label: "Last 12 months" },
    { value: "custom", label: "Custom Range", disabled: true },
  ];

  return (
  <>
    <div className="flex flex-col pt-2 gap-4">
      <div className="flex h-[50px] items-center text-lg justify-center gap-[100px] bg-gray-50 rounded px-4">
        <div className="flex items-center gap-3 text-xl font-semibold text-teal-300 rounded px-3 py-2">
          <span className="font-semibold">Date Range :</span>
          <span className="font-semibold">{fromDate ? formatDateToDD_MMM_YYYY(fromDate) : "--"}</span>
          <span className="font-semibold">TO</span>
          <span className="font-semibold">{toDate ? formatDateToDD_MMM_YYYY(toDate) : "--"}</span>
        </div>
        <div className="relative">
          <button type="button" onClick={handleOpenDateRange} className="ml-3 inline-flex items-center justify-center w-8 h-8 text-teal-300">
            <ReactSVG src="/icons/medicalSummary/Notes/CalendarIcon.svg" className="w-6 h-6" />
          </button>
          {showDateMenu && (
            <div className="absolute left-2 mt-2  w-56 bg-white border border-gray-100 rounded shadow z-10">
              <ul className="text-sm font-normal text-gray-800">
                {dateRangeOptions.map((opt) => (
                  <li key={opt.value}>
                    <button
                      type="button"
                      disabled={!!opt.disabled}
                      className={`w-full text-left py-[10px] px-5 ${opt.disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"} ${date === opt.value ? "bg-blue-100" : ""}`}
                      onClick={() => {
                        if (opt.disabled) return;
                        if (opt.value === "custom") { setShowDateMenu(false); return; }
                        setDateRange(opt.value);
                        const { from, to } = computeRangeForOption(opt.value);
                        setFromDate(from);
                        setToDate(to);
                        setShowDateMenu(false);
                        getNotesData();
                      }}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="flow-root bg-white">
            <div className="overflow-x-auto max-h-[770px] overflow-y-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden border border-gray-50 rounded-[4px]">
                  <table className="min-w-full divide-y divide-gray-100 overflow-x-auto overflow-y-auto">
                    <tbody className="divide-y divide-gray-50">

                      {/* Notes Section */}
                      <tr className="text-teal-300 text-lg bg-gray-20">
                        <th className="px-6 py-3 gap-2 text-left font-semibold sm:pl-6">Patient Chart Notes</th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">
                          <span className="inline-flex items-center gap-3">
                            Date Performed
                            <span className="inline-flex w-[26px] h-[26px] items-center justify-center text-gray-400 rounded">
                              <ArrowLongDownIcon
                                onClick={() => handleSortOrder("date", "desc")}
                                className={`w-4 h-4 ${sort === "date" && order === "desc" ? "text-REELTeal" : "text-gray-600"} cursor-pointer`}
                              />
                              <ArrowLongUpIcon
                                onClick={() => handleSortOrder("date", "asc")}
                                className={`w-4 h-4 ${sort === "date" && order === "asc" ? "text-REELTeal" : "text-gray-600"} -ml-1.5 cursor-pointer`}
                              />
                            </span>
                          </span>
                        </th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">
                          <span className="inline-flex items-center gap-3">Performed By
                            <span className="inline-flex w-[26px] h-[26px] items-center justify-center text-gray-400 rounded">
                              <ArrowLongDownIcon
                                onClick={() => handleSortOrder("performedBy", "desc")}
                                className={`w-4 h-4 ${sort === "performedBy" && order === "desc" ? "text-REELTeal" : "text-gray-600"} cursor-pointer`}
                              />
                              <ArrowLongUpIcon
                                onClick={() => handleSortOrder("performedBy", "asc")}
                                className={`w-4 h-4 ${sort === "performedBy" && order === "asc" ? "text-REELTeal" : "text-gray-600"} -ml-1.5 cursor-pointer`}
                              />
                            </span>
                          </span>
                        </th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">Documentation Form</th>
                      </tr>
                      {groupedNotes.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="py-4 font-medium text-center text-gray-900">No Notes Found</td>
                        </tr>
                      ) : (
                        groupedNotes.map((group) => {
                          const rowId = group.rowId;
                          const item = group.notes?.[0];
                          return (
                            <tr key={rowId} className={`transition-colors duration-300 group hover:bg-blue-100 ${selectedRowId === rowId ? "bg-blue-100" : ""}`}>
                              <td className="py-4 pl-6 pr-12  text-base font-semibold text-gray-800 whitespace-pre-wrap">
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center">
                                    <input
                                      id={`notesRow-${rowId}`}
                                      name="notesRow"
                                      type="radio"
                                      value={rowId}
                                      checked={selectedRowId === rowId}
                                      onChange={() => handleRowSelect(group)}
                                      className="w-5 h-5 cursor-pointer text-teal-50 focus:ring-transparent"
                                    />
                                    <label
                                      htmlFor={`notesRow-${rowId}`}
                                      className="block ml-3 text-teal-600 text-body-md cursor-pointer"
                                    >
                                      <span className="font-semibold text-gray-800 leading-tight text-md-body">Patient Chart</span>
                                    </label>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-3 gap-2 text-base font-semibold text-gray-700 whitespace-pre-wrap">
                                {group.dateKey && !String(group.dateKey).startsWith('nodate-')
                                  ? formatDateToDD_MMM_YYYY(new Date(group.dateKey))
                                  : (item?.systemPeriod ? formatDateToDD_MMM_YYYY(new Date(item.systemPeriod)) : "N/A")}
                              </td>
                              <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700 whitespace-pre-wrap">{group.performedBy || 'Vesna Spadafora'}</td>
                              <td className="px-6 py-4 gap-2">
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    className="w-10 h-10 flex items-center justify-center"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Do not change selection when clicking the documentation icon.
                                        // Only navigate when the row is already selected.
                                        if (selectedRowId !== rowId) return;
                                        const serviceDateIso = group?.dateKey && !String(group.dateKey).startsWith('nodate-')
                                          ? group.dateKey
                                          : (item?.systemPeriod ? new Date(item.systemPeriod).toISOString().split('T')[0] : null);
                                        const dateQuery = serviceDateIso ? `&serviceDate=${encodeURIComponent(serviceDateIso)}` : '';
                                        const url = `/patient/${patientId}/medical-summary/NOTES/${rowId}/form/patientChart?selectedRowId=${rowId}${dateQuery}`;
                                        navigate(url);
                                      }}
                                  >
                                    <ReactSVG src={selectedRowId === rowId ? "/icons/medicalSummary/Notes/NotesActive.svg" : "/icons/medicalSummary/Notes/NotesInActive.svg"} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    {showNoteModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" onClick={() => setShowNoteModal(false)} />
        <div className="relative max-w-3xl w-full bg-white rounded shadow-lg p-4 z-10">
          <div className="flex justify-end">
            <button onClick={() => setShowNoteModal(false)} className="text-gray-500">Close</button>
          </div>
          <div className="mt-2">
            {modalNote ? (
              <NotesList
                notepad={[modalNote]}
                patient={patient}
                startDateTime={modalNote?.systemPeriod || modalNote?.date}
                serviceId={modalNote?.serviceId || null}
                clinicalServiceName={modalNote?.notepadType || null}
                showOnlyPatientChart={true}
              />
            ) : (
              <div>No note selected</div>
            )}
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default Notes;
